/***
 * 高さを揃える
 *
 * 仕様
 * - ist-js__setHeight: 高さを揃えるコンテナ
 * - ist-js__setHeightTarget: 高さを揃える要素
 *
 * 使い方
 * <div class="ist-js__setHeight">
 *   <div class="ist-js__setHeightTarget">
 *     <p>コンテンツ</p>
 *   </div>
 *   <div class="ist-js__setHeightTarget">
 *     <p>コンテンツ</p>
 *   </div>
 * </div>
 *
 * 追加仕様01
 * - data-set で指定されている要素（基本はid）に data-set-style-property で指定されている css 変数を設定する
 */

/**
 * 高さ揃えに関するエラーを扱うカスタムエラークラス
 */
class SetHeightError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'SetHeightError';
  }
}

/**
 * 要素の高さを揃えるクラス
 */
export class SetHeight {
  /** クラス名の定数 */
  public static readonly CLASS_NAMES = {
    base: 'ist-js__setHeight',
    target: 'ist-js__setHeightTarget',
  } as const;

  private container: Element;
  private targets: NodeListOf<HTMLElement>;
  /** 要素のリサイズを監視するObserver */
  private resizeObserver: ResizeObserver = new ResizeObserver(
    this.debounce(() => {
      this.adjustHeights();
      this.applyDataSetProperties(); // 追加仕様01の実行
    }, 200)
  );

  /**
   * コンストラクタ
   * @param container 高さを揃える要素を含むコンテナ要素
   */
  constructor(container: Element) {
    try {
      this.container = container;
      this.targets = container.querySelectorAll(
        `.${SetHeight.CLASS_NAMES.target}`
      );

      if (this.validateElements()) {
        this.init();
      }
    } catch (error) {
      if (error instanceof SetHeightError) {
        console.error(`高さ調整の初期化に失敗しました: ${error.message}`);
      } else {
        console.error(
          '高さ調整の初期化中に予期せぬエラーが発生しました:',
          error
        );
      }
      throw error;
    }
  }

  /**
   * ターゲット要素の検証
   * @returns 検証結果
   */
  private validateElements(): boolean {
    if (this.targets.length < 2) {
      throw new SetHeightError(
        `クラス ${SetHeight.CLASS_NAMES.target} を持つ要素が2つ以上必要です`
      );
    }
    return true;
  }

  /**
   * 初期化処理
   */
  private init(): void {
    this.setupResizeObserver();
    this.adjustHeights();
    this.applyDataSetProperties(); // 追加仕様01の初期実行
    window.addEventListener(
      'resize',
      this.debounce(this.adjustHeights.bind(this), 200)
    );
  }

  /**
   * ResizeObserverの設定
   */
  private setupResizeObserver(): void {
    this.targets.forEach((target) => {
      this.resizeObserver.observe(target);
    });
  }

  /**
   * 要素の高さを調整
   */
  private adjustHeights(): void {
    // リセット
    this.targets.forEach((target) => {
      target.style.height = 'auto';
    });

    // 要素を配列に変換
    const elements = Array.from(this.targets);

    // 行ごとにグループ化
    const rows: HTMLElement[][] = [];
    elements.forEach((el) => {
      const offsetTop = el.offsetTop;
      let row = rows.find((r) => r[0].offsetTop === offsetTop);
      if (row) {
        row.push(el);
      } else {
        rows.push([el]);
      }
    });

    // 各行ごとに最大高さを計算して適用
    rows.forEach((row) => {
      const maxHeight = row.reduce(
        (max, el) => Math.max(max, el.offsetHeight),
        0
      );
      row.forEach((el) => {
        el.style.height = `${maxHeight}px`;
      });
    });
  }

  /**
   * data-set 属性を持つ要素に対して CSS 変数を適用
   */
  private applyDataSetProperties(): void {
    // data-set 属性を持つ要素を取得
    this.targets.forEach((target) => {
      const dataSetElement = target.closest('[data-set]');
      if (!dataSetElement) return;

      // data-set で指定された要素のセレクタを取得
      const targetSelector = dataSetElement.getAttribute('data-set');
      if (!targetSelector) return;

      // data-set-style-property で指定された CSS 変数名を取得
      const styleProperty = dataSetElement.getAttribute(
        'data-set-style-property'
      );
      if (!styleProperty) return;

      // 対象要素を取得
      const targetElement = document.querySelector<HTMLElement>(targetSelector);
      if (!targetElement) {
        console.warn(
          `セレクター "${targetSelector}" に一致する要素が見つかりませんでした`
        );
        return;
      }

      // ist-js__setHeightTarget の高さを取得して
      // 指定された要素の CSS 変数として設定
      targetElement.style.setProperty(
        styleProperty,
        `${target.offsetHeight}px`
      );
    });
  }

  /**
   * 関数のデバウンス処理
   * @param func デバウンスする関数
   * @param wait 待機時間（ミリ秒）
   * @returns デバウンスされた関数
   */
  private debounce(func: Function, wait: number): (...args: any[]) => void {
    let timeout: number;
    return (...args: any[]) => {
      clearTimeout(timeout);
      timeout = window.setTimeout(() => func.apply(this, args), wait);
    };
  }

  /**
   * インスタンスの破棄
   */
  public destroy(): void {
    window.removeEventListener(
      'resize',
      this.debounce(this.adjustHeights.bind(this), 200)
    );
    this.resizeObserver.disconnect();
    this.targets.forEach((target) => {
      target.style.height = 'auto';
    });
  }
}

/**
 * 複数の高さ揃えグループを管理するマネージャークラス
 */
export class SetHeightManager {
  /** 高さ揃えグループの配列 */
  private groups: SetHeight[] = [];

  constructor() {
    this.init();
  }

  /**
   * 初期化処理
   */
  private init(): void {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.initializeGroups();
      });
    } else {
      this.initializeGroups();
    }
  }

  /**
   * 高さ揃えグループの初期化
   */
  private initializeGroups(): void {
    const containers = document.querySelectorAll(
      `.${SetHeight.CLASS_NAMES.base}`
    );
    containers.forEach((container) => {
      try {
        this.groups.push(new SetHeight(container));
      } catch (error) {
        console.error(`高さ揃えグループの初期化に失敗しました:`, error);
      }
    });
  }

  /**
   * 高さ揃えグループの取得
   * @returns 高さ揃えグループの配列
   */
  public getGroups(): SetHeight[] {
    return this.groups;
  }

  /**
   * すべての高さ揃えグループの破棄
   */
  public destroy(): void {
    this.groups.forEach((group) => group.destroy());
    this.groups = [];
  }
}
